import { Container, Flex, Text, Heading, Grid, Box } from '@radix-ui/themes';
import { useTenantContext } from '@trashlab/sitesapp/src/contexts/use-tenant-context';
import { DumpsterCard } from '@trashlab/sitesapp/src/templates/default/components/dumpster-rentals/dumpster-card';
import { getFirstLetterUpperCaseAfterSpaces } from '@trashlab/sitesapp/src/components/shared/text';
import { useFilteredSkus } from '@trashlab/sitesapp/src/hooks/use-filtered-skus';
import { useVariantValue } from '@trashlab/sitesapp/src/lib/utils/get-variant-value';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

export const DumpsterTypes = ({
  defaultType = '',
  onClose,
}: {
  defaultType?: string;
  onClose?: () => void;
}) => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address as any);

  const { filteredSkus } = useFilteredSkus(
    website?.organization.id ?? '',
    defaultType,
  );

  const residentialServiceLabel = useVariantValue(
    'RESIDENTIAL_SERVICE_LABEL',
    'Residential Service',
  );
  const commercialServiceLabel = useVariantValue(
    'COMMERCIAL_SERVICE_LABEL',
    'Front Load Service',
  );
  const rolloffServiceLabel = useVariantValue(
    'ROLLOFF_SERVICE_LABEL',
    'Rolloff Dumpsters',
  );
  const portableToiletServiceLabel = useVariantValue(
    'PORTABLE_TOILET_SERVICE_LABEL',
    'Portable Toilets',
  );

  // Get service label for current type
  const getServiceLabel = (type: string) => {
    switch (type) {
      case 'residential':
        return residentialServiceLabel;
      case 'commercial':
        return commercialServiceLabel;
      case 'rolloff':
        return rolloffServiceLabel;
      case 'portableToilet':
        return portableToiletServiceLabel;
      default:
        return 'Dumpster';
    }
  };

  // Get data variable for current type
  const getServiceLabelVariable = (type: string) => {
    switch (type) {
      case 'residential':
        return 'RESIDENTIAL_SERVICE_LABEL';
      case 'commercial':
        return 'COMMERCIAL_SERVICE_LABEL';
      case 'rolloff':
        return 'ROLLOFF_SERVICE_LABEL';
      case 'portableToilet':
        return 'PORTABLE_TOILET_SERVICE_LABEL';
      default:
        return '';
    }
  };

  const serviceTypeLabel = defaultType
    ? getServiceLabel(defaultType)
    : 'Dumpster';

  const dumpsterTypesSubtitle = useVariantValue(
    'DUMPSTER_TYPES_SUBTITLE',
    `Top-rated ${
      defaultType === 'portableToilet' ? 'portable toilet' : 'dumpster'
    } rental in ${orgAddress?.city ?? 'San Francisco'}, ${
      orgAddress?.state ?? 'CA'
    }`,
  );

  return (
    <Container size="4" p="8" id="dumpster-types">
      <Flex direction="column" gap="4">
        <Text
          size="2"
          weight="bold"
          className="text-neutral-400"
          data-variable="DUMPSTER_TYPES_SUBTITLE"
        >
          {dumpsterTypesSubtitle}
        </Text>

        <Heading as="h2" size="8" data-variable={getServiceLabelVariable(defaultType)}>
          {serviceTypeLabel} rental
        </Heading>

        <Box className="relative">
          <Grid columns={{ initial: '1', sm: '2', md: '3' }} gap="4">
            {filteredSkus.filter(Boolean).map((sku) => (
              <DumpsterCard key={sku!.id} sku={sku!} onClose={onClose} />
            ))}
          </Grid>
        </Box>
      </Flex>
    </Container>
  );
};

export default DumpsterTypes;
